export const TENCENT_AIGC_MATERIAL_IDS_OPTION = '__tencentAigcMaterialIds';
export const TENCENT_ASSET_REF_PREFIX = 'asset://';

export type TencentAigcAssetType = 'Image' | 'Video' | 'Audio';
export type TencentAigcMaterialStatus = 'Processing' | 'Active' | 'Failed';
export type TencentAigcMaterialTaskStatus = 'Processing' | 'Finished' | 'Failed';

type JsonRecord = Record<string, unknown>;

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getByPath(value: unknown, path: string): unknown {
  let current = value;
  for (const segment of path.split('.')) {
    if (!isRecord(current)) return undefined;
    current = current[segment];
  }
  return current;
}

function firstStringAtPaths(value: unknown, paths: string[]): string | undefined {
  for (const path of paths) {
    const candidate = getByPath(value, path);
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
  }
  return undefined;
}

function firstNumberAtPaths(value: unknown, paths: string[]): number | undefined {
  for (const path of paths) {
    const candidate = getByPath(value, path);
    if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate;
  }
  return undefined;
}

function findNestedString(
  value: unknown,
  keys: Set<string>,
  depth = 0,
): string | undefined {
  if (depth > 5 || value === null || value === undefined) return undefined;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findNestedString(item, keys, depth + 1);
      if (found) return found;
    }
    return undefined;
  }
  if (!isRecord(value)) return undefined;

  for (const [key, candidate] of Object.entries(value)) {
    if (keys.has(key.toLowerCase()) && typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }
  for (const candidate of Object.values(value)) {
    const found = findNestedString(candidate, keys, depth + 1);
    if (found) return found;
  }
  return undefined;
}

function findNestedNumber(
  value: unknown,
  keys: Set<string>,
  depth = 0,
): number | undefined {
  if (depth > 5 || value === null || value === undefined) return undefined;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findNestedNumber(item, keys, depth + 1);
      if (found !== undefined) return found;
    }
    return undefined;
  }
  if (!isRecord(value)) return undefined;

  for (const [key, candidate] of Object.entries(value)) {
    if (keys.has(key.toLowerCase()) && typeof candidate === 'number' && Number.isFinite(candidate)) {
      return candidate;
    }
  }
  for (const candidate of Object.values(value)) {
    const found = findNestedNumber(candidate, keys, depth + 1);
    if (found !== undefined) return found;
  }
  return undefined;
}

export function getTencentAssetIdFromReference(url: string): string | undefined {
  if (!url.toLowerCase().startsWith(TENCENT_ASSET_REF_PREFIX)) return undefined;
  const assetId = url.slice(TENCENT_ASSET_REF_PREFIX.length).trim();
  return assetId || undefined;
}

export function buildTencentMaterialLookupKey(
  url: string,
  assetType: TencentAigcAssetType,
): string {
  // 与 ensure / buildAigcMaterialInputs 统一 trim，避免 content URL 带空白时映射 miss。
  return `${assetType}::${url.trim()}`;
}

export function extractTencentMaterialAssetId(response: unknown): string | undefined {
  return (
    firstStringAtPaths(response, [
      'Response.AssetId',
      'Response.Result.AssetId',
      'Response.Result.Id',
      'Response.Asset.AssetId',
      'Response.Asset.Id',
      'Response.AssetInfo.AssetId',
      'Response.AssetInfo.Id',
      'Response.Material.AssetId',
      'Response.Material.Id',
      'Response.MaterialInfo.AssetId',
      'Response.MaterialInfo.Id',
    ]) ?? findNestedString(response, new Set(['assetid']))
  );
}

export function extractTencentMaterialTaskId(response: unknown): string | undefined {
  return (
    firstStringAtPaths(response, [
      'Response.TaskId',
      'Response.Result.TaskId',
      'Response.Task.TaskId',
    ]) ?? findNestedString(response, new Set(['taskid']))
  );
}

export function extractTencentMaterialTaskStatus(response: unknown): {
  status: TencentAigcMaterialTaskStatus;
  rawStatus?: string;
  message?: string;
} {
  const rawStatus =
    firstStringAtPaths(response, [
      'Response.Status',
      'Response.CreateAigcMaterialTask.Status',
      'Response.AigcMaterialTask.Status',
      'Response.Result.Status',
    ]) ?? findNestedString(response, new Set(['status', 'taskstatus']));
  const errCode =
    firstNumberAtPaths(response, [
      'Response.CreateAigcMaterialTask.ErrCode',
      'Response.AigcMaterialTask.ErrCode',
      'Response.Result.ErrCode',
      'Response.ErrCode',
    ]) ?? findNestedNumber(response, new Set(['errcode', 'errorcode']));
  const normalized = rawStatus?.toLowerCase().replace(/[\s_-]/g, '');
  const finishedStatuses = new Set(['done', 'finish', 'finished', 'success', 'succeeded']);
  const failedStatuses = new Set(['aborted', 'error', 'fail', 'failed']);

  const status: TencentAigcMaterialTaskStatus =
    (errCode !== undefined && errCode !== 0) ||
    (normalized !== undefined && failedStatuses.has(normalized))
      ? 'Failed'
      : normalized !== undefined && finishedStatuses.has(normalized)
        ? 'Finished'
        : 'Processing';
  const message =
    firstStringAtPaths(response, [
      'Response.CreateAigcMaterialTask.Message',
      'Response.AigcMaterialTask.Message',
      'Response.Result.Message',
      'Response.ErrMsg',
    ]) ??
    findNestedString(
      response,
      new Set(['failedreason', 'failuremessage', 'errormessage', 'message', 'errmsg']),
    );

  return {
    status,
    ...(rawStatus ? { rawStatus } : {}),
    ...(status === 'Failed' && message ? { message } : {}),
  };
}

export function extractTencentMaterialStatus(response: unknown): {
  status: TencentAigcMaterialStatus;
  rawStatus?: string;
  message?: string;
} {
  const rawStatus =
    firstStringAtPaths(response, [
      'Response.Status',
      'Response.Result.Status',
      'Response.Asset.Status',
      'Response.AssetInfo.Status',
      'Response.Material.Status',
      'Response.MaterialInfo.Status',
      'Response.AigcMaterial.Status',
    ]) ?? findNestedString(response, new Set(['status', 'reviewstatus']));

  const normalized = rawStatus?.toLowerCase().replace(/[\s_-]/g, '');
  const activeStatuses = new Set([
    'active',
    'approved',
    'available',
    'done',
    'finish',
    'finished',
    'pass',
    'passed',
    'success',
    'succeeded',
  ]);
  const failedStatuses = new Set([
    'blocked',
    'error',
    'fail',
    'failed',
    'inactive',
    'reject',
    'rejected',
  ]);

  const status: TencentAigcMaterialStatus = normalized && activeStatuses.has(normalized)
    ? 'Active'
    : normalized && failedStatuses.has(normalized)
      ? 'Failed'
      : 'Processing';

  const message =
    firstStringAtPaths(response, [
      'Response.Result.Error.Message',
      'Response.Result.FailedReason',
      'Response.Asset.Error.Message',
      'Response.Asset.FailedReason',
      'Response.Material.Error.Message',
      'Response.Material.FailedReason',
      'Response.Error.Message',
    ]) ??
    findNestedString(
      response,
      new Set(['failedreason', 'failuremessage', 'errormessage', 'message']),
    );

  return {
    status,
    ...(rawStatus ? { rawStatus } : {}),
    ...(status === 'Failed' && message ? { message } : {}),
  };
}

function normalizeAssetType(value: unknown): TencentAigcAssetType {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'video') return 'Video';
  if (normalized === 'audio') return 'Audio';
  return 'Image';
}

/**
 * 将腾讯渠道任务载荷里的原始 URL 替换为已审核的 asset:// 引用。
 * 映射由 Inngest 审核步骤通过私有 provider option 注入。
 */
export function applyTencentMaterialReferences(
  input: JsonRecord,
  providerOptions?: Record<string, any>,
): JsonRecord {
  const materialIds = providerOptions?.[TENCENT_AIGC_MATERIAL_IDS_OPTION];
  if (!isRecord(materialIds)) return input;

  const resolveUrl = (url: unknown, assetType: TencentAigcAssetType): unknown => {
    if (typeof url !== 'string' || !url) return url;
    const existingAssetId = getTencentAssetIdFromReference(url);
    if (existingAssetId) return `${TENCENT_ASSET_REF_PREFIX}${existingAssetId}`;
    const assetId = materialIds[buildTencentMaterialLookupKey(url, assetType)];
    return typeof assetId === 'string' && assetId
      ? `${TENCENT_ASSET_REF_PREFIX}${assetId}`
      : url;
  };

  let changed = false;
  const next: JsonRecord = { ...input };

  if (Array.isArray(input.FileInfos)) {
    next.FileInfos = input.FileInfos.map((item) => {
      if (!isRecord(item)) return item;
      const assetType = normalizeAssetType(item.Category);
      const url = resolveUrl(item.Url, assetType);
      if (url === item.Url) return item;
      changed = true;
      return { ...item, Url: url };
    });
  }

  if (typeof input.LastFrameUrl === 'string') {
    const lastFrameUrl = resolveUrl(input.LastFrameUrl, 'Image');
    if (lastFrameUrl !== input.LastFrameUrl) {
      next.LastFrameUrl = lastFrameUrl;
      changed = true;
    }
  }

  return changed ? next : input;
}
